// EclipseInsight, personalized eclipse analysis
// Route: /eclipses

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Lock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { safeInvoke } from '../lib/safeInvoke';
import { TextSkeleton } from '../components/shared/TextSkeleton';
import { EmptyState } from '../components/ui/EmptyState';

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

interface EclipseEvent {
  id:           string;
  eclipse_date: string;
  type:         'solar' | 'lunar';
  sign:         string;
  degree:       number | null;
  visibility:   string | null;
  description:  string | null;
  year:         number;
}

interface EclipseAnalysis {
  personal_impact:  string;
  activated_house:  number;
  themes:           string[];
  preparation:      string;
}

// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────

const MONTHS_BR = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

function formatDateBR(d: string): string {
  const [y, m, day] = d.split('-');
  return `${parseInt(day)} de ${MONTHS_BR[parseInt(m) - 1]} de ${y}`;
}

function formatDateShort(d: string): string {
  const [, m, day] = d.split('-');
  return `${parseInt(day)} ${MONTHS_BR[parseInt(m) - 1].slice(0, 3)}`;
}

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + 'T00:00:00');
  return Math.ceil((target.getTime() - today.getTime()) / 86400000);
}

function isPast(dateStr: string, today: string): boolean {
  return dateStr < today;
}

const TYPE_META = {
  solar: { emoji: '🌑', label: 'Solar',  color: '#fbbf24' },
  lunar: { emoji: '🌕', label: 'Lunar',  color: '#60a5fa' },
};

// ─────────────────────────────────────────
// Countdown
// ─────────────────────────────────────────

function Countdown({ days }: { days: number }) {
  if (days < 0) return null;
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'baseline', gap: '0.3rem',
      padding: '0.5rem 1rem',
      borderRadius: 'var(--radius-md)',
      background: 'rgba(251,191,36,0.1)',
      border: '1px solid rgba(251,191,36,0.3)',
    }}>
      <span style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--color-gold)', fontFamily: 'var(--font-display)' }}>
        {days}
      </span>
      <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
        dias
      </span>
    </div>
  );
}

// ─────────────────────────────────────────
// Eclipse card
// ─────────────────────────────────────────

function EclipseCard({
  event,
  today,
  isPremium,
  onAnalyze,
  analysis,
  generating,
}: {
  event:     EclipseEvent;
  today:     string;
  isPremium: boolean;
  onAnalyze: (id: string) => void;
  analysis:  EclipseAnalysis | null;
  generating: boolean;
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const meta   = TYPE_META[event.type];
  const past   = isPast(event.eclipse_date, today);
  const days   = daysUntil(event.eclipse_date);

  return (
    <div style={{
      borderRadius: 'var(--radius-lg)',
      border: `1px solid ${past ? 'rgba(120,120,120,0.2)' : meta.color + '33'}`,
      background: past ? 'rgba(180,180,180,0.07)' : meta.color + '08',
      marginBottom: '0.75rem',
      overflow: 'hidden',
      opacity: past ? 0.8 : 1,
    }}>
      <button
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          gap: '0.75rem', padding: '1rem 1.25rem',
          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
        onClick={() => setExpanded(v => !v)}
      >
        <span style={{ fontSize: '1.5rem' }}>{meta.emoji}</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
              Eclipse {meta.label} em {event.sign}
            </span>
            {event.visibility && (
              <span style={{
                fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em',
                padding: '0.15rem 0.5rem', borderRadius: '999px',
                background: meta.color + '22', color: meta.color,
              }}>{event.visibility}</span>
            )}
          </div>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
            {formatDateShort(event.eclipse_date)} {event.year}
            {!past && days >= 0 && ` · em ${days} dias`}
            {past && ' · passado'}
          </span>
        </div>
      </button>

      {expanded && (
        <div style={{ padding: '0 1.25rem 1.25rem' }}>
          {event.description && (
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.6 }}>
              {event.description}
            </p>
          )}

          {!isPremium ? (
            <div style={{ position: 'relative' }}>
              <div style={{ filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.6 }}>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                  Este eclipse ativa sua Casa 7 natal, trazendo temas de parcerias e relações.
                  Planetas natais em Touro e Escorpião são especialmente ativados.
                  Prepare-se para revelações importantes nesta área.
                </p>
              </div>
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '0.5rem',
              }}>
                <Lock size={18} color="var(--color-gold)" />
                <button className="btn btn-primary btn-sm" onClick={() => navigate('/pricing')}>
                  Ver análise pessoal
                </button>
              </div>
            </div>
          ) : !past && !analysis && !generating ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => onAnalyze(event.id)}
              style={{ gap: '0.4rem' }}
            >
              <Sparkles size={14} />
              Gerar análise pessoal
            </button>
          ) : generating ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
              <Sparkles size={14} className="animate-pulse" />
              Gerando sua análise...
            </div>
          ) : analysis ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* House badge */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.3rem 0.7rem',
                borderRadius: 'var(--radius-md)',
                background: meta.color + '22',
                color: meta.color,
                fontSize: 'var(--text-caption)',
                fontWeight: 600,
                alignSelf: 'flex-start',
              }}>
                Casa {analysis.activated_house} natal ativada
              </div>

              <div>
                <h4 style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>
                  Impacto pessoal
                </h4>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {analysis.personal_impact}
                </p>
              </div>

              {analysis.themes?.length > 0 && (
                <div>
                  <h4 style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>
                    Temas
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {analysis.themes.map((theme, i) => (
                      <span key={i} style={{
                        padding: '0.2rem 0.6rem',
                        borderRadius: '999px',
                        background: meta.color + '22',
                        color: meta.color,
                        fontSize: 'var(--text-caption)',
                      }}>{theme}</span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>
                  Preparação
                </h4>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {analysis.preparation}
                </p>
              </div>

              <button
                className="btn btn-secondary btn-sm"
                onClick={() => onAnalyze(event.id)}
                style={{ alignSelf: 'flex-start' }}
              >
                Regenerar
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// Main page
// ─────────────────────────────────────────

export function EclipseInsight() {
  useSystemUsageTracker('eclipse');
  const { profile } = useAuth();
  const navigate    = useNavigate();

  const [events,    setEvents]    = useState<EclipseEvent[]>([]);
  const [analyses,  setAnalyses]  = useState<Record<string, EclipseAnalysis>>({});
  const [generating, setGenerating] = useState<string | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());

  const today     = new Date().toISOString().slice(0, 10);
  const isPremium = profile?.subscription_tier === 'monthly' || profile?.subscription_tier === 'annual' || profile?.unlimited_access === true;

  // Load eclipse events
  useEffect(() => {
    supabase
      .from('eclipse_events')
      .select('*')
      .gte('year', new Date().getFullYear())
      .order('eclipse_date', { ascending: true })
      .then(({ data }) => {
        if (data) setEvents(data as EclipseEvent[]);
        setLoading(false);
      });
  }, []);

  // Load existing analyses
  useEffect(() => {
    if (!profile?.id || events.length === 0) return;
    supabase
      .from('user_eclipse_analyses')
      .select('eclipse_id, personal_impact, activated_house, themes, preparation')
      .eq('user_id', profile.id)
      .in('eclipse_id', events.map(e => e.id))
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, EclipseAnalysis> = {};
        for (const row of data) {
          map[row.eclipse_id] = {
            personal_impact: row.personal_impact,
            activated_house: row.activated_house,
            themes:          row.themes ?? [],
            preparation:     row.preparation,
          };
        }
        setAnalyses(map);
      });
  }, [profile?.id, events]);

  const handleAnalyze = useCallback(async (eclipseId: string) => {
    if (!profile?.birth_date) { navigate('/birth-chart'); return; }
    setGenerating(eclipseId);
    setError(null);

    const result = await safeInvoke<{ analysis: EclipseAnalysis }>(
      'eclipse-analysis',
      {
        body:     { eclipse_id: eclipseId, regenerate: !!analyses[eclipseId] },
        timeout:  45_000,
        fallback: null as unknown as { analysis: EclipseAnalysis },
      },
    );

    if (result && !result.fromFallback && result.data?.analysis) {
      setAnalyses(prev => ({ ...prev, [eclipseId]: result.data.analysis }));
    } else {
      setError('Não foi possível gerar a análise. Verifique seu mapa natal e tente novamente.');
    }
    setGenerating(null);
  }, [profile?.birth_date, analyses, navigate]);

  // Next upcoming eclipse
  const nextEclipse = events.find(e => !isPast(e.eclipse_date, today));
  const filteredEvents = events.filter(e => e.year === yearFilter);
  const availableYears = [...new Set(events.map(e => e.year))].sort();

  if (!profile?.birth_date) {
    return (
      <div className="page-container">
        <EmptyState
          emoji="🌑"
          title="Mapa natal necessário"
          subtitle="Para ver como os eclipses ativam seu mapa, primeiro calcule seu Mapa Natal."
          cta="Calcular Mapa Natal"
          ctaAction={() => navigate('/birth-chart')}
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          🌑 Eclipse Insight
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Descubra como cada eclipse ativa seu mapa natal
        </p>
      </div>

      {/* Next eclipse countdown */}
      {nextEclipse && (
        <div style={{
          padding: '1.25rem',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--surface-card)',
          border: '1px solid var(--border-subtle)',
          marginBottom: '1.5rem',
        }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            Próximo eclipse
          </p>
          <p style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-body)', marginBottom: '0.75rem' }}>
            {TYPE_META[nextEclipse.type].emoji} Eclipse {TYPE_META[nextEclipse.type].label} em {nextEclipse.sign}, {formatDateBR(nextEclipse.eclipse_date)}
          </p>
          <Countdown days={daysUntil(nextEclipse.eclipse_date)} />
        </div>
      )}

      {/* Year filter */}
      {availableYears.length > 1 && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
          {availableYears.map(y => (
            <button
              key={y}
              className={yearFilter === y ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
              onClick={() => setYearFilter(y)}
            >
              {y}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="error-banner" style={{ marginBottom: '1rem' }}>{error}</div>
      )}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)', padding: '1rem', background: 'var(--surface-card)' }}>
              <TextSkeleton lines={2} />
            </div>
          ))}
        </div>
      ) : filteredEvents.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>
          Nenhum eclipse para {yearFilter}.
        </p>
      ) : (
        <div>
          {filteredEvents.map(event => (
            <EclipseCard
              key={event.id}
              event={event}
              today={today}
              isPremium={isPremium}
              onAnalyze={handleAnalyze}
              analysis={analyses[event.id] ?? null}
              generating={generating === event.id}
            />
          ))}
        </div>
      )}

      {!isPremium && (
        <div style={{
          marginTop: '2rem', padding: '1.25rem',
          borderRadius: 'var(--radius-lg)',
          background: 'rgba(245,200,66,0.06)',
          border: '1px solid rgba(245,200,66,0.2)',
          textAlign: 'center',
        }}>
          <Lock size={20} color="var(--color-gold)" style={{ margin: '0 auto 0.5rem' }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.75rem' }}>
            Assinantes têm acesso às análises pessoais de cada eclipse
          </p>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/pricing')}>
            Assinar agora
          </button>
        </div>
      )}
    </div>
  );
}
